// Runtime origin resolver — single source of truth for "which host is the
// user actually on right now?".
//
// WHY: `process.env.REACT_APP_BACKEND_URL` is baked at build time. When the
// build is deployed to a NEW domain (e.g. ring.tacoloco.food in production
// vs the preview URL it was built against), every URL constructed from
// that env var points to the wrong host. The fix is to ALWAYS prefer
// `window.location.origin` at runtime — it's the only value that follows
// the user across deploys, custom domains and white-label setups.
//
// Used by:
//   • services/api.js  → axios baseURL for /api/* calls
//   • Anywhere we display a URL/QR pointing back to ourselves (consent QR,
//     onboarding link, public registration, customer dashboard magic link…)

const ENV_BACKEND = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

function safeWindowOrigin() {
  if (typeof window === 'undefined' || !window.location) return '';
  return window.location.origin.replace(/\/$/, '');
}

/**
 * The host we should use for ANY user-facing URL we render in the page
 * (QR codes, share links, copy-link modals, etc).
 *
 * Always returns `window.location.origin` when available, which guarantees
 * the URL points to the same domain the user is currently browsing.
 *
 * Fallbacks to the build-time env (preview URL) only during SSR or
 * pre-mount, which we don't actually do — kept defensively.
 */
export function getPublicOrigin() {
  return safeWindowOrigin() || ENV_BACKEND;
}

/**
 * The base URL for axios calls to the backend.
 *
 * Strategy:
 *   • If `window.location.origin` matches the build-time env, use the env
 *     (preserves the current preview behaviour).
 *   • If they DIFFER (typical production deploy where the .env still
 *     carries the preview URL), switch to the current origin. The
 *     Kubernetes ingress on every Emergent deploy co-routes `/api/*` to
 *     the backend, so `${currentOrigin}/api` always resolves correctly.
 *   • Final fallback to the env (server-side rendering, never used today).
 */
export function getApiBase() {
  const cur = safeWindowOrigin();
  if (!cur) return `${ENV_BACKEND}/api`;
  if (ENV_BACKEND && ENV_BACKEND === cur) return `${ENV_BACKEND}/api`;
  return `${cur}/api`;
}

/**
 * Build a public URL on the SAME ORIGIN as the page (e.g.
 * `${origin}/consent/{token}`, `${origin}/c/{slug}`). Use this whenever
 * the URL is meant to be scanned / shared / printed and lead back to
 * this app. NEVER trust a `consent_url` returned by the backend for
 * display — the backend may have built it from a stale X-Forwarded-Host.
 */
export function buildPublicUrl(path) {
  const origin = getPublicOrigin();
  if (!path) return origin;
  return `${origin}${path.startsWith('/') ? '' : '/'}${path}`;
}

/**
 * Drop-in replacement for `process.env.REACT_APP_BACKEND_URL`.
 *
 * Returns the host that fetch/axios calls should target. Equivalent to
 * `getApiBase()` minus the trailing `/api`, so callsites like
 *   `${process.env.REACT_APP_BACKEND_URL}/api/customers/${id}/qrcode`
 * keep working unchanged after a global find-replace.
 */
export function getBackendUrl() {
  const cur = safeWindowOrigin();
  if (!cur) return ENV_BACKEND;
  if (ENV_BACKEND && ENV_BACKEND === cur) return ENV_BACKEND;
  return cur;
}

/**
 * Take any absolute URL emitted by the backend and rewrite its origin to
 * the CURRENT page origin, keeping pathname + query + hash intact.
 *
 * Used by /admin/preview-links and /superadmin/preview-links so that the
 * "share this link with your customer" URLs always match the dominion the
 * brand-admin is currently browsing — no surprises like "I copied a
 * preview link in production by mistake".
 *
 * Safe on relative URLs (returned as-is) and on bad inputs.
 */
export function rebaseToCurrentOrigin(url) {
  if (!url || typeof url !== 'string') return url;
  const origin = safeWindowOrigin();
  if (!origin) return url;
  try {
    // Relative URL → just prepend origin
    if (!/^https?:\/\//i.test(url)) {
      return `${origin}${url.startsWith('/') ? '' : '/'}${url}`;
    }
    const u = new URL(url);
    const cur = new URL(origin);
    u.protocol = cur.protocol;
    u.host = cur.host;
    return u.toString();
  } catch {
    return url;
  }
}
